import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { cx, sizeClass, type Hue } from './shared';

// ---------------------------------------------------------------- spinner

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl';
  hue?: Hue;
  /** Take the surrounding text colour instead of a hue. */
  current?: boolean;
  /** Screen-reader text. Default "Loading". */
  label?: string;
}

export function Spinner({ size, hue, current, label = 'Loading', className, ...rest }: SpinnerProps) {
  return (
    <span
      role="status"
      aria-label={label}
      className={cx('spinner', sizeClass('spinner', size), hue && `spinner--${hue}`, current && 'spinner--current', className)}
      {...rest}
    />
  );
}

/** A spinner with a message, centred. */
export function Loading({
  children = 'Loading…',
  inline,
  fill,
  size,
  hue,
  className,
}: {
  children?: ReactNode;
  inline?: boolean;
  /** Fill the parent (e.g. a whole panel or page area). */
  fill?: boolean;
  size?: SpinnerProps['size'];
  hue?: Hue;
  className?: string;
}) {
  return (
    <div className={cx('loading', inline && 'loading--inline', fill && 'loading--fill', className)} aria-live="polite">
      <span className={cx('spinner', sizeClass('spinner', size ?? (inline ? 'sm' : 'base')), hue && `spinner--${hue}`)} aria-hidden="true" />
      <span className="loading__text">{children}</span>
    </div>
  );
}

// --------------------------------------------------------------- skeleton

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  /** text: one line (use lines for several); heading; circle (avatar); block (default). */
  shape?: 'block' | 'text' | 'heading' | 'circle';
  /** For text: how many lines; the last is shorter. */
  lines?: number;
  /** Width as a utility class value: 25, 50, 75 or 100 (%). */
  width?: 25 | 50 | 75 | 100;
}

/** A pulsing placeholder standing in for content that is loading. */
export function Skeleton({ shape = 'block', lines = 1, width, className, ...rest }: SkeletonProps) {
  const classes = (w?: number) => cx('skeleton', shape !== 'block' && `skeleton--${shape}`, w !== undefined && `w-${w}`, className);
  if (shape === 'text' && lines > 1) {
    return (
      <span aria-hidden="true" style={{ display: 'block' }}>
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} className={classes(i === lines - 1 ? 75 : width ?? 100)} {...rest} />
        ))}
      </span>
    );
  }
  return <span aria-hidden="true" className={classes(width)} {...rest} />;
}

// --------------------------------------------------------------- progress

export interface ProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** 0–100. Omit for an indeterminate (sweeping) bar. */
  value?: number;
  hue?: Hue;
  size?: 'sm' | 'base' | 'lg';
  striped?: boolean;
  /** Text above the bar; the value shows on the right. */
  label?: ReactNode;
  /** Right-hand text; default "{value}%". */
  valueText?: ReactNode;
}

export function Progress({ value, hue, size, striped, label, valueText, className, style, ...rest }: ProgressProps) {
  const indeterminate = value === undefined;
  const clamped = Math.max(0, Math.min(100, value ?? 0));
  const bar = (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : clamped}
      className={cx(
        'progress',
        hue && `progress--${hue}`,
        sizeClass('progress', size),
        striped && 'progress--striped',
        indeterminate && 'progress--indeterminate',
        className,
      )}
      style={{ ...style, '--progress': `${clamped}%` } as CSSProperties}
      {...rest}
    >
      <div className="progress__bar" />
    </div>
  );
  if (!label) return bar;
  return (
    <div>
      <div className="progress__label">
        <span>{label}</span>
        {!indeterminate && <span>{valueText ?? `${Math.round(clamped)}%`}</span>}
      </div>
      {bar}
    </div>
  );
}

// ---------------------------------------------------------------- overlay

/**
 * Covers its positioned parent (card, table wrap, panel) with a dimmed layer
 * and a spinner while it reloads. Render it inside the parent; give the parent
 * aria-busy while it's active.
 */
export function LoadingOverlay({ active = true, children, hue }: { active?: boolean; children?: ReactNode; hue?: Hue }) {
  if (!active) return null;
  return (
    <div className="loading-overlay" role="status">
      <span className={cx('spinner', hue && `spinner--${hue}`)} aria-hidden="true" />
      {children ?? <span className="visually-hidden">Loading</span>}
    </div>
  );
}
